/**
 * Game Socket Client
 * Singleton socket.io-client wrapper with strict typed events
 * Implements Pydantic v2 extra='forbid' defense via stripUndefined
 */

import { io, Socket } from 'socket.io-client';
import { stripUndefined } from '../../utils';
import type { ConnectionState, SocketConfig } from '../../types/socket';
import { DEFAULT_SOCKET_CONFIG } from '../../types/socket';

/**
 * Internal socket state
 */
interface InternalSocketState {
  socket: Socket<Record<string, unknown>, Record<string, unknown>> | null;
  config: SocketConfig;
  connectionState: ConnectionState;
  listeners: Map<string, Set<Function>>;
  reconnectAttempts: number;
  isIntentionalDisconnect: boolean;
}

/**
 * Singleton instance
 */
let internalState: InternalSocketState | null = null;

/**
 * Get or create singleton state
 */
function getState(): InternalSocketState {
  if (!internalState) {
    internalState = {
      socket: null,
      config: { ...DEFAULT_SOCKET_CONFIG },
      connectionState: 'disconnected',
      listeners: new Map(),
      reconnectAttempts: 0,
      isIntentionalDisconnect: false,
    };
  }
  return internalState;
}

/**
 * Initialize socket with configuration
 * Must be called before connect()
 */
export function initializeSocket(config: Partial<SocketConfig>): void {
  const state = getState();
  
  // If already initialized with socket, disconnect first
  if (state.socket) {
    state.socket.disconnect();
    state.socket = null;
  }
  
  state.config = { ...DEFAULT_SOCKET_CONFIG, ...config };
  state.connectionState = 'disconnected';
  state.reconnectAttempts = 0;
  state.isIntentionalDisconnect = false;
  state.listeners.clear();
}

/**
 * Get current socket instance (creates if not exists)
 */
function getSocket(): Socket<Record<string, unknown>, Record<string, unknown>> {
  const state = getState();
  
  if (!state.socket) {
    if (!state.config.url) {
      throw new Error('Socket not initialized. Call initializeSocket() first with URL.');
    }
    
    const socketOptions = {
      auth: state.config.auth,
      reconnection: state.config.reconnection,
      reconnectionAttempts: state.config.reconnectionAttempts,
      reconnectionDelay: state.config.reconnectionDelay,
      reconnectionDelayMax: state.config.reconnectionDelayMax,
      timeout: state.config.timeout,
      transports: state.config.transports,
      autoConnect: state.config.autoConnect,
    } as any; // Cast to any since io() accepts merged ManagerOptions + SocketOptions
    
    state.socket = io(state.config.url, socketOptions) as Socket<Record<string, unknown>, Record<string, unknown>>;
    
    // Attach connection lifecycle handlers
    attachLifecycleHandlers(state.socket);
  }
  
  return state.socket;
}

/**
 * Attach core lifecycle handlers
 */
function attachLifecycleHandlers(socket: Socket<Record<string, unknown>, Record<string, unknown>>): void {
  const state = getState();
  
  socket.on('connect', () => {
    state.connectionState = 'connected';
    state.reconnectAttempts = 0;
    state.isIntentionalDisconnect = false;
    console.log('[GameSocket] Connected:', socket.id);
  });
  
  socket.on('disconnect', (reason: string) => {
    state.connectionState = 'disconnected';
    console.log('[GameSocket] Disconnected:', reason);
  });
  
  socket.on('connect_error', (error: Error) => {
    state.connectionState = 'error';
    console.error('[GameSocket] Connection error:', error.message);
  });
  
  socket.on('reconnect_attempt', (attemptNumber: number) => {
    state.connectionState = 'reconnecting';
    state.reconnectAttempts = attemptNumber;
    console.log('[GameSocket] Reconnection attempt:', attemptNumber);
  });
  
  socket.on('reconnect', (attemptNumber: number) => {
    state.connectionState = 'connected';
    state.reconnectAttempts = 0;
    console.log('[GameSocket] Reconnected after', attemptNumber, 'attempts');
  });
  
  socket.on('reconnect_failed', () => {
    state.connectionState = 'error';
    console.error('[GameSocket] Reconnection failed after max attempts');
  });
  
  // Handle server reboot notice
  socket.on('server:rebooting', (payload: { grace_seconds: number; message: string }) => {
    console.warn('[GameSocket] Server rebooting:', payload.message);
    state.connectionState = 'disconnected';
    state.isIntentionalDisconnect = true;
    socket.disconnect();
  });
}

/**
 * Connect to socket server
 */
export function connectSocket(): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocket();
    const state = getState();
    
    if (state.connectionState === 'connected') {
      resolve();
      return;
    }
    
    state.isIntentionalDisconnect = false;
    
    const onConnect = () => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onError);
      resolve();
    };
    
    const onError = (error: Error) => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onError);
      reject(error);
    };
    
    socket.on('connect', onConnect);
    socket.on('connect_error', onError);
    
    if (!socket.active) {
      socket.connect();
    }
  });
}

/**
 * Disconnect socket intentionally
 */
export function disconnectSocket(): void {
  const state = getState();
  
  if (state.socket) {
    state.isIntentionalDisconnect = true;
    state.socket.disconnect();
    state.socket = null;
    state.connectionState = 'disconnected';
    state.listeners.clear();
  }
}

/**
 * Typed emit with stripUndefined (Pydantic extra='forbid' defense)
 */
export const typedEmit = <K extends string>(
  event: K,
  ...args: unknown[]
): void => {
  const socket = getSocket();
  
  // Strip undefined from payload (defense against Pydantic v2 extra='forbid')
  const sanitizedArgs = args.map((arg) => {
    if (arg && typeof arg === 'object' && !Array.isArray(arg)) {
      return stripUndefined(arg as Record<string, unknown>);
    }
    return arg;
  });
  
  // Cast socket to any for emit to bypass strict generic typing
  (socket as any).emit(event, ...sanitizedArgs);
};

/**
 * Typed on listener with automatic tracking for cleanup
 */
export const typedOn = <K extends string>(
  event: K,
  listener: (...args: any[]) => void
): void => {
  const socket = getSocket();
  const state = getState();
  
  // Cast to satisfy socket.io-client's strict typing
  socket.on(event, listener as any);
  
  // Track listener for cleanup
  if (!state.listeners.has(event)) {
    state.listeners.set(event, new Set());
  }
  state.listeners.get(event)!.add(listener);
};

/**
 * Typed off listener
 */
export const typedOff = <K extends string>(
  event: K,
  listener?: (...args: any[]) => void
): void => {
  const socket = getSocket();
  const state = getState();
  
  if (listener) {
    socket.off(event, listener as any);
    state.listeners.get(event)?.delete(listener);
  } else {
    // Remove all listeners for this event
    const listeners = state.listeners.get(event);
    if (listeners) {
      listeners.forEach((l) => socket.off(event, l as any));
      listeners.clear();
    }
  }
};

/**
 * Remove all tracked listeners
 */
export function removeAllTrackedListeners(): void {
  const socket = getSocket();
  const state = getState();
  
  state.listeners.forEach((listeners, event) => {
    listeners.forEach((l) => socket.off(event, l));
    listeners.clear();
  });
  state.listeners.clear();
}

/**
 * Get current connection state
 */
export function getConnectionState(): ConnectionState {
  return getState().connectionState;
}

/**
 * Check if socket is connected
 */
export function isConnected(): boolean {
  return getState().connectionState === 'connected';
}

/**
 * Get socket ID
 */
export function getSocketId(): string | undefined {
  return getState().socket?.id;
}

/**
 * Convenience methods for common game events
 */

// Match lifecycle
export function joinMatch(playerId: string, matchId: string): void {
  typedEmit('match:join', { player_id: playerId, match_id: matchId });
}

export function attemptReconnect(matchId: string): void {
  typedEmit('reconnect:attempt', { match_id: matchId });
}

// Matchmaking
export function joinQueue(): void {
  typedEmit('queue:join', {});
}

export function leaveQueue(): void {
  typedEmit('queue:leave', {});
}

// Setup phase
export function submitSetup(color: 'white' | 'black', placements: Array<{ square: string; type: string }>): void {
  typedEmit('setup:submit', { color, placements });
}

// In-game
export function submitMove(from: string, to: string, promotion?: 'q' | 'r' | 'b' | 'n'): void {
  typedEmit('move:submit', { from, to, promotion });
}

export function resign(): void {
  typedEmit('resign', {});
}

// Friend rooms
export function createRoom(mode: 'match' | 'tournament'): void {
  typedEmit('room:create', { mode });
}

export function joinRoom(roomCode: string): void {
  typedEmit('room:join', { room_code: roomCode });
}

export function leaveRoom(roomCode?: string): void {
  typedEmit('room:leave', { room_code: roomCode });
}

export function pickSeat(roomCode: string, seatIndex: number): void {
  typedEmit('room:pick_seat', { room_code: roomCode, seat_index: seatIndex });
}

export function kickMember(roomCode: string, targetUserId: string): void {
  typedEmit('room:kick', { room_code: roomCode, target_user_id: targetUserId });
}

export function startRoom(roomCode: string): void {
  typedEmit('room:start', { room_code: roomCode });
}

export function requestRoomState(roomCode: string): void {
  typedEmit('room:state', { room_code: roomCode });
}

// Spectating
export function joinSpectate(matchId: string, roomCode: string): void {
  typedEmit('spectate:join', { match_id: matchId, room_code: roomCode });
}

export function leaveSpectate(): void {
  typedEmit('spectate:leave', {});
}

// Tournaments
export function tournamentReady(tournamentId: string, matchId: string): void {
  typedEmit('tournament:ready', { tournament_id: tournamentId, match_id: matchId });
}

/**
 * Event listener helpers for common patterns
 */

export function onMatchJoined(listener: (payload: { match_id: string; color: 'white' | 'black' }) => void): void {
  typedOn('match:joined', listener as (...args: unknown[]) => void);
}

export function onMatchStarted(listener: (payload: unknown) => void): void {
  typedOn('match:started', listener as (...args: unknown[]) => void);
}

export function onGameState(listener: (payload: unknown) => void): void {
  typedOn('game:state', listener as (...args: unknown[]) => void);
}

export function onMoveApplied(listener: (payload: unknown) => void): void {
  typedOn('move:applied', listener as (...args: unknown[]) => void);
}

export function onPieceCaptured(listener: (payload: unknown) => void): void {
  typedOn('piece:captured', listener as (...args: unknown[]) => void);
}

export function onCheckWarning(listener: (payload: { color: 'white' | 'black' }) => void): void {
  typedOn('check:warning', listener as (...args: unknown[]) => void);
}

export function onGameOver(listener: (payload: unknown) => void): void {
  typedOn('game:over', listener as (...args: unknown[]) => void);
}

export function onQueueJoined(listener: (payload: unknown) => void): void {
  typedOn('queue:joined', listener as (...args: unknown[]) => void);
}

export function onQueueLeft(listener: (payload: { message: string }) => void): void {
  typedOn('queue:left', listener as (...args: unknown[]) => void);
}

export function onMatchFound(listener: (payload: unknown) => void): void {
  typedOn('match:found', listener as (...args: unknown[]) => void);
}

export function onSetupAccepted(listener: (payload: unknown) => void): void {
  typedOn('setup:accepted', listener as (...args: unknown[]) => void);
}

export function onSetupRejected(listener: (payload: { color: 'white' | 'black'; reason: string }) => void): void {
  typedOn('setup:rejected', listener as (...args: unknown[]) => void);
}

export function onRoomCreated(listener: (payload: unknown) => void): void {
  typedOn('room:created', listener as (...args: unknown[]) => void);
}

export function onRoomState(listener: (payload: unknown) => void): void {
  typedOn('room:state', listener as (...args: unknown[]) => void);
}

export function onRoomLeft(listener: (payload: unknown) => void): void {
  typedOn('room:left', listener as (...args: unknown[]) => void);
}

export function onRoomKicked(listener: (payload: { room_code: string }) => void): void {
  typedOn('room:kicked', listener as (...args: unknown[]) => void);
}

export function onRoomStarted(listener: (payload: { room_code: string; match_id: string }) => void): void {
  typedOn('room:started', listener as (...args: unknown[]) => void);
}

export function onOpponentDisconnected(listener: (payload: { player: 'white' | 'black'; grace_until: number }) => void): void {
  typedOn('opponent:disconnected', listener as (...args: unknown[]) => void);
}

export function onOpponentReconnected(listener: (payload: { player: 'white' | 'black' }) => void): void {
  typedOn('opponent:reconnected', listener as (...args: unknown[]) => void);
}

export function onServerRebooting(listener: (payload: { grace_seconds: number; message: string }) => void): void {
  typedOn('server:rebooting', listener as (...args: unknown[]) => void);
}

export function onError(listener: (payload: { reason: string; details?: Record<string, unknown> }) => void): void {
  const errorEvents = [
    'error:server_rebooting',
    'error:invalid_payload',
    'error:unknown_match',
    'error:not_in_match',
    'error:not_authenticated',
    'error:wrong_phase',
    'error:feature_disabled',
    'error:service_unavailable',
    'error:gate_locked',
    'error:already_queued',
    'error:not_joined',
    'error:no_active_match',
    'error:lock_busy',
    'error:corrupted_state',
    'error:not_your_turn',
    'error:internal',
    'error:not_found',
    'error:not_room_owner',
    'error:room_not_ready',
    'error:not_room_member',
    'error:cannot_spectate_own_match',
    'error:spectate_not_available',
  ] as const;
  
  errorEvents.forEach((event) => {
    typedOn(event, listener as (...args: unknown[]) => void);
  });
}

/**
 * Cleanup all specific error listeners
 */
export function offError(listener: (payload: { reason: string; details?: Record<string, unknown> }) => void): void {
  const errorEvents = [
    'error:server_rebooting',
    'error:invalid_payload',
    'error:unknown_match',
    'error:not_in_match',
    'error:not_authenticated',
    'error:wrong_phase',
    'error:feature_disabled',
    'error:service_unavailable',
    'error:gate_locked',
    'error:already_queued',
    'error:not_joined',
    'error:no_active_match',
    'error:lock_busy',
    'error:corrupted_state',
    'error:not_your_turn',
    'error:internal',
    'error:not_found',
    'error:not_room_owner',
    'error:room_not_ready',
    'error:not_room_member',
    'error:cannot_spectate_own_match',
    'error:spectate_not_available',
    'error:server_rebooting',
  ] as const;
  
  errorEvents.forEach((event) => {
    typedOff(event, listener as (...args: unknown[]) => void);
  });
}

/**
 * Socket instance for direct access (use sparingly)
 */
export function getRawSocket(): Socket<Record<string, unknown>, Record<string, unknown>> | null {
  return getState().socket;
}